import React, { createContext, useContext, useReducer } from "react";
import {
  DocumentTypeState,
  DocumentTypeAction,
} from "../../../types/documentTypes";

const initialState: DocumentTypeState = {
  currentTab: "basic",
  Name: "",
  Description: "",
  CustomInstructions: "",
  Version: 1,
  BaseDocumentTypeID: null,
  Status: "Draft",
  IsLatestVersion: true,
  document_type_sources: [],
  document_type_inputs: [],
  document_type_sections: [],
  deletedSourceIds: [],
};

const DocumentTypeContext = createContext<
  | {
      state: DocumentTypeState;
      dispatch: React.Dispatch<DocumentTypeAction>;
    }
  | undefined
>(undefined);

const documentTypeReducer = (
  state: DocumentTypeState,
  action: DocumentTypeAction,
): DocumentTypeState => {
  switch (action.type) {
    case "SET_TAB":
      return { ...state, currentTab: action.payload };
    case "UPDATE_NAME":
      return { ...state, Name: action.payload };
    case "UPDATE_DESCRIPTION":
      return { ...state, Description: action.payload };
    case "UPDATE_CUSTOM_INSTRUCTIONS":
      return { ...state, CustomInstructions: action.payload };
    case "UPDATE_SOURCE_DOCUMENTS":
      return {
        ...state,
        document_type_sources: action.payload.documents,
        deletedSourceIds: action.payload.deletedSourceIds,
      };
    case "UPDATE_FIELDS":
      return { ...state, document_type_inputs: action.payload };
    case "ADD_FIELD":
      return {
        ...state,
        document_type_inputs: [...state.document_type_inputs, action.payload],
      };
    case "UPDATE_FIELD":
      return {
        ...state,
        document_type_inputs: state.document_type_inputs.map((field, index) =>
          index === action.payload.index
            ? { ...field, ...action.payload.updates }
            : field,
        ),
      };
    case "REMOVE_FIELD":
      return {
        ...state,
        document_type_inputs: state.document_type_inputs.filter(
          (_, index) => index !== action.payload,
        ),
      };
    case "ADD_SOURCE_DOCUMENT":
      return {
        ...state,
        document_type_sources: [...state.document_type_sources, action.payload],
      };
    case "UPDATE_SOURCE_DOCUMENT":
      return {
        ...state,
        document_type_sources: state.document_type_sources.map((doc, index) =>
          index === action.payload.index
            ? { ...doc, ...action.payload.updates }
            : doc,
        ),
      };
    case "REMOVE_SOURCE_DOCUMENT":
      return {
        ...state,
        document_type_sources: state.document_type_sources.filter(
          (_, index) => index !== action.payload,
        ),
      };
    case "UPDATE_SECTIONS":
      return { ...state, document_type_sections: action.payload };
    case "UPDATE_SECTIONS_FUNCTIONAL":
      return {
        ...state,
        document_type_sections: action.payload(state.document_type_sections),
      };
    case "ADD_SECTION":
      return {
        ...state,
        document_type_sections: [
          ...state.document_type_sections,
          action.payload,
        ],
      };
    case "UPDATE_SECTION":
      return {
        ...state,
        document_type_sections: state.document_type_sections.map((section) =>
          section.DocumentTypeSectionID === action.payload.id
            ? { ...section, ...action.payload.updates }
            : section,
        ),
      };
    case "REMOVE_SECTION":
      return {
        ...state,
        document_type_sections: state.document_type_sections.filter(
          (section) => section.DocumentTypeSectionID !== action.payload,
        ),
      };
    case "UPDATE_STATUS":
      return { ...state, Status: action.payload };
    case "SET_VERSION_INFO":
      return {
        ...state,
        Version: action.payload.Version,
        BaseDocumentTypeID: action.payload.BaseDocumentTypeID,
        IsLatestVersion: action.payload.IsLatestVersion,
      };
    default:
      return state;
  }
};

export const DocumentTypeProvider: React.FC<{
  children: React.ReactNode;
  initialTab?: string;
}> = ({ children, initialTab }) => {
  const [state, dispatch] = useReducer(documentTypeReducer, {
    ...initialState,
    currentTab: initialTab ?? initialState.currentTab,
  });

  return (
    <DocumentTypeContext.Provider value={{ state, dispatch }}>
      {children}
    </DocumentTypeContext.Provider>
  );
};

export const useDocumentType = () => {
  const context = useContext(DocumentTypeContext);
  if (context === undefined) {
    throw new Error(
      "useDocumentType must be used within a DocumentTypeProvider",
    );
  }
  return context;
};
